import { DEPARTMENT_ID } from '@@types/common';
import Member from '@@types/Member';

import { LANGUAGES, LOCALE_CODE } from '~/constants';
import { MemberStatusField } from '~/constants/member';
import i18n from '~/i18n';
import { getNoMemberTitle } from '~/services/channelService';
import { mMaybe } from '~/utils/monadUtil';
import { includes, isEmpty } from 'lodash';
import {
  all,
  allPass,
  always,
  anyPass,
  complement,
  concat,
  converge,
  curry,
  descend,
  equals,
  find,
  identity,
  ifElse,
  join,
  juxt,
  map,
  pathOr,
  pipe,
  prop,
  propEq,
  propOr,
  props,
  propSatisfies,
  reject,
  sortWith,
  values,
  when,
} from 'ramda';
import defaultTo from 'ramda/es/defaultTo';
import objOf from 'ramda/es/objOf';

import { isNonEmpty, isPathEmpty, safeFind } from '../utils/fnUtil/fnUtil';
import { isWithinNow } from './dateUtil';
import { getDefaultLocale, getDefaultTimezone } from './runEnvironmentUtil';

const NE_TENANT_ID = '1387695619080878080';

export const getMemberNickname = pipe<any, string, string>(
  propOr('', 'nickname'),
  when(isNonEmpty, (nickname) => `[${nickname}]`)
);

export const getMemberName: (param: { name?: string }) => string = propOr('', 'name');

export const getMemberFullName = converge<[{ name?: string; nickname?: string }], string, string, string>(concat, [
  getMemberName,
  getMemberNickname,
]);

export const getMembersFullName = pipe(map(getMemberFullName), join(', '));

export const getPrivateTitle = curry((myId, isMeChannel, render, memberIds) => {
  return pipe(
    ifElse(allPass([(ids) => ids.length === 1, propEq(0, myId), always(isMeChannel)]), identity, reject(equals(myId))),
    ifElse(isEmpty, getNoMemberTitle, render)
  )(memberIds);
});

export const getDefaultOrgId: (member: Member) => string = pipe(
  prop('organizationMemberRoleMap'),
  values,
  safeFind(prop('defaultFlag')),
  mMaybe('', prop('organizationId'))
);

export const isGuest = propEq('tenantMemberRole', 'guest');

export const isSubMember = propEq('tenantMemberRole', 'subMember');

export const isNeTenant = propEq('tenantId', NE_TENANT_ID);

// isVacationEnabled :: Object -> Boolean
export const isVacationEnabled = pathOr(false, ['vacation', 'displayProfileFlag']);

// isVacationDisabled :: Object -> Boolean
export const isVacationDisabled = complement(isVacationEnabled);

export const isVacationPeriodCoverToday = pipe(
  prop('vacation'),
  converge<any, any, any, any>(isWithinNow, [prop('startedAt'), prop('endedAt')])
);

// isInVacation :: Object -> Boolean
export const isInVacation = allPass([isVacationEnabled, isVacationPeriodCoverToday]);

// isEmptyBeginOfficeHour :: Member -> Boolean
const isEmptyBeginOfficeHour = isPathEmpty(['officeHours', 'begin']);

// isEmptyEndOfficeHour :: Member -> Boolean
const isEmptyEndOfficeHour = isPathEmpty(['officeHours', 'end']);

// getBeginEndOfficeHour :: Member -> Boolean
const getBeginEndOfficeHour = pipe<any, any, any>(prop('officeHours'), props(['begin', 'end']));

// isZeroOfficeHours :: Member -> Boolean
const isZeroOfficeHours = pipe(getBeginEndOfficeHour, all(equals('00:00')));

// isInvalidOfficeHours :: Member -> Boolean
const isInvalidOfficeHours = anyPass([isEmptyBeginOfficeHour, isEmptyEndOfficeHour, isZeroOfficeHours]);

// hasValidOfficeHours :: Member -> Boolean
export const hasValidOfficeHours = complement(isInvalidOfficeHours);

export const isLeaver = propEq('tenantMemberRole', 'leaver');

export const isNonLeaver = complement(isLeaver);

export const isDormant = propSatisfies(
  (value: MemberStatusField) => value === MemberStatusField.DORMANT || value === MemberStatusField.AUTO_DORMANT,
  'status'
);

export const leaverWrapText = () => `(${i18n.t('leaver', '탈퇴한 사용자')})`;
export const dormantWrapText = () => `(${i18n.t('dormant.text', '비활성화')})`;

export const getLeaverText: (member: { tenantMemberRole?: string }) => string = ifElse(
  isLeaver,
  leaverWrapText,
  always('')
);

export const getDormantText: (member: { status?: string }) => string = ifElse(isDormant, dormantWrapText, always(''));

export const getLeaverOrDormantText = pipe<{ tenantMemberRole?: string; status?: string }[], string[], string>(
  juxt<[{ tenantMemberRole?: string; status?: string }], string, string>([getLeaverText, getDormantText]),
  join('')
);

export const getDefaultDepartmentMember = pipe(
  pathOr([], ['additional', 'departmentMemberList']),
  find(propEq('primaryFlag', true))
);

export const getDefaultDepartmentId: (member: Member) => null | DEPARTMENT_ID = pipe(
  getDefaultDepartmentMember,
  propOr(null, 'departmentId')
) as (member: Member) => null | DEPARTMENT_ID;

export const getRankId = pathOr(null, ['additional', 'rankId']);

export const getSortedDepartmentMemberList = pipe(
  pathOr([], ['additional', 'departmentMemberList']),
  sortWith([descend(prop('primaryFlag'))])
);

// findValidLocale :: String -> String
export const findValidLocale = (locale) => {
  return find((lang) => includes(lang, locale), map(prop('value'), LANGUAGES));
};

export const parseUserCodeForHDC = (userCode = '') => {
  return /^[0-9]*$/.test(userCode) ? userCode : userCode.slice(4);
};

export const fixTimezone = (user: Member) => {
  return when(pipe(propOr(null, 'timezoneName'), isEmpty), pipe(getDefaultTimezone, objOf('timezoneName')))(user);
};

export const fixLocale = (user: Member) => {
  return when(
    pipe(propOr(null, 'locale'), isEmpty),
    pipe(getDefaultLocale, findValidLocale, defaultTo(LOCALE_CODE.EN_US), objOf('locale'))
  )(user);
};
